import Alpine from 'alpinejs';

Alpine.data('textslider', () => ({
    init() {
        new TextSlider(this.$root);
    },
}));

/**
 * TextSlider class to create infinite text slider animations.
 * @class TextSlider
 * @param {HTMLElement} element - The element to create the text slider on.
 * @param {number} duration - The duration of the animation in milliseconds, defaults to 3000, can be overwritten by the "data-duration" attribute.
 * @requires 3 or more elements with the class "text-slider__word" inside the element.
 * @example new TextSlider(document.querySelector('.text-slider'));
 * */
class TextSlider {
    constructor(element, duration = 3000) {
        this.element = element;
        this.class = {
            word: 'text-slider__word',
            wordVisible: 'text-slider__word--is-current',
            wordPrev: 'text-slider__word--is-prev',
            wordNext: 'text-slider__word--is-next',
        };

        this.state = 'stopped';
        this.wordsArray = Array.from(this.element.querySelectorAll(`.${this.class.word}`));
        this.element.style.maxWidth = `${this.getWidth()}px`;

        if (!this.wordsArray || this.wordsArray.length <= 1) return;

        this.duration = parseInt(this.element.dataset.duration) || duration;
        this.interval = null;
        this.currentWord = this.wordsArray[0];
        this.nextWord = this.wordsArray[1];

        // initial start
        if (this.isInViewport()) {
            this.play();
        }

        // play only when in viewport
        window.addEventListener('scroll', () => {
            this[this.isInViewport() ? 'play' : 'stop']();
        });

        // change max width on resize
        window.addEventListener('resize', () => {
            this.element.style.maxWidth = `${this.getWidth()}px`;
        });
    }

    isInViewport() {
        const rect = this.element.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const vertInView = rect.top <= windowHeight && rect.top + rect.height >= 0;
        const horInView = rect.left <= windowWidth && rect.left + rect.width >= 0;

        return vertInView && horInView;
    }

    play() {
        if (this.state === 'playing') return;

        this.state = 'playing';
        this.change();
        this.interval = setInterval(() => {
            this.change();
        }, this.duration);
    }

    stop() {
        if (!this.interval || this.state === 'stopped') return;

        this.state = 'stopped';
        clearInterval(this.interval);
    }

    getWidth() {
        // get max width of all words
        return Math.max(...this.wordsArray.map((word) => word.getBoundingClientRect().width));
    }

    isFirst(word) {
        return this.wordsArray.indexOf(word) === 0;
    }

    isLast(word) {
        return this.wordsArray.indexOf(word) === this.wordsArray.length - 1;
    }

    getPrev() {
        // if first word, return last
        return this.isFirst(this.currentWord)
            ? this.wordsArray[this.wordsArray.length - 1]
            : this.wordsArray[this.wordsArray.indexOf(this.currentWord) - 1];
    }

    getNext() {
        // if last word, return first
        return this.isLast(this.currentWord)
            ? this.wordsArray[0]
            : this.wordsArray[this.wordsArray.indexOf(this.currentWord) + 1];
    }

    show(word) {
        if (!word) return;
        word.classList.add(this.class.wordVisible);
    }

    hide(word) {
        if (!word) return;
        word.classList.remove(this.class.wordVisible);
    }

    change() {
        // hide current and show next
        this.hide(this.currentWord);
        this.show(this.nextWord);

        // clear prev next classes
        this.getPrev().classList.remove(this.class.wordPrev);
        this.getNext().classList.remove(this.class.wordNext);

        // switch current and next
        this.currentWord = this.nextWord;
        this.nextWord = this.getNext();

        // add prev next classes
        this.getPrev().classList.add(this.class.wordPrev);
        this.getNext().classList.add(this.class.wordNext);
    }
}
